<script setup lang="ts">
import { useNuxtApp } from "nuxt/app"
import { computed, ref, watch } from "vue"
import { Doughnut } from "vue-chartjs"
import { ArcElement, Chart as ChartJS, Legend, Title, Tooltip } from "chart.js"

ChartJS.register(ArcElement, Tooltip, Legend, Title)

const { $api, $translate, $getColor } = useNuxtApp()

const stats = ref({})
const loading = ref(false)
const year = ref(null)

const recordsChart = ref({})
const measurementChart = ref({})

const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
        legend: { display: false },
        tooltip: { enabled: true },
    },
}

if ($api) {
    loading.value = true
    $api.setRequest({ url: "stats", method: "GET" }).then((r) => {
        stats.value = r || {}
        loading.value = false
    })
}

watch(year, () => {
    loading.value = true
    $api.setRequest({ url: `stats${year.value ? `?year=${year.value}` : ""}`, method: "GET" }).then((r) => {
        stats.value = r || {}
        loading.value = false
    })
})

watch(stats, (newVal) => {
    if (newVal.records_count) {
        recordsChart.value = {
            labels: Object.keys(newVal.records_count).map(l => $translate(`dossiers.status.${l}`)),
            datasets: [{
                data: Object.values(newVal.records_count),
                backgroundColor: Object.keys(newVal.records_count).map(k => $getColor(`dossiers.status.${k}`)),
                borderWidth: 0,
            }],
        }
    }
    if (newVal.records) {
        measurementChart.value = {
            labels: Object.keys(newVal.records).map(l => $translate(`dossiers.status.${l}`)),
            datasets: [{
                data: Object.values(newVal.records),
                backgroundColor: Object.keys(newVal.records).map(k => $getColor(`dossiers.status.${k}`)),
                borderWidth: 0,
            }],
        }
    }
}, { deep: true })

const years = []
let thisYear = 2022
const currentYear = new Date().getFullYear()
while (thisYear <= currentYear) {
    years.push(thisYear++)
}

const totalRecords = computed(() => {
    if (!stats.value.records_count)
        return 0
    return Object.values(stats.value.records_count).reduce((sum, val) => sum + Number(val), 0)
})

const totalMeasurement = computed(() => {
    if (!stats.value.records)
        return 0
    return Object.values(stats.value.records).reduce((sum, val) => sum + Number(val), 0).toFixed(2)
})

const recordsLegendItems = computed(() => {
    if (!stats.value.records_count)
        return []
    return Object.keys(stats.value.records_count).map(k => ({
        label: $translate(`dossiers.status.${k}`),
        color: $getColor(`dossiers.status.${k}`),
    }))
})

const measurementLegendItems = computed(() => {
    if (!stats.value.records)
        return []
    return Object.keys(stats.value.records).map(k => ({
        label: $translate(`dossiers.status.${k}`),
        color: $getColor(`dossiers.status.${k}`),
    }))
})

const currentDate = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })

const missionStatuses = [
    { key: "estimate", label: "Devis", color: "#F59E0B" },
    { key: "pending", label: "En attente", color: "#6B7280" },
    { key: "ongoing", label: "En cours", color: "#3B82F6" },
    { key: "done", label: "Réalisée", color: "#10B981" },
    { key: "terminated", label: "Résiliée", color: "#EF4444" },
]

const totalMissions = computed(() => {
    if (!stats.value.missions)
        return 0
    return Object.values(stats.value.missions).reduce((sum, val) => sum + Number(val), 0)
})
</script>

<template>
    <v-container fluid style="background-color: #F8FAFC; min-height: 100vh;">
        <div class="d-flex flex-column flex-md-row justify-space-between align-md-end mb-6">
            <div>
                <div class="text-h4 font-weight-bold text-primary">
                    Statistiques {{ year || 'toutes années' }}
                </div>
            </div>
            <div class="d-flex align-center mt-4 mt-md-0">
                <div class="text-body-2 text-grey mr-6 d-none d-sm-block">
                    Données actualisées le {{ currentDate }}
                </div>
                <div style="width: 200px">
                    <v-select
                        v-model="year"
                        :items="years"
                        placeholder="Filtrer par année"
                        density="compact"
                        variant="solo-filled"
                        flat
                        hide-details
                        clearable
                        prepend-inner-icon="mdi-calendar"
                    />
                </div>
            </div>
        </div>

        <v-skeleton-loader v-if="loading" height="200" type="card" class="mb-6" />

        <v-card v-else class="pa-6 mb-6" elevation="1" rounded="lg">

            <v-row>
                <v-col cols="12" md="4">
                    <div class="text-overline text-grey mb-4">
                        NOMBRE DE DOSSIERS
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Archived') }" /> Archivés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records_count?.Archived || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Transferred') }" /> Versés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records_count?.Transferred || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between pb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Destroyed') }" /> Détruits
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records_count?.Destroyed || 0 }}
                        </div>
                    </div>
                </v-col>

                <v-col cols="12" md="4">
                    <div class="text-overline text-grey mb-4">
                        MÉTRAGE (M)
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Archived') }" /> Archivés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records?.Archived || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Transferred') }" /> Versés
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records?.Transferred || 0 }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between pb-2">
                        <div class="d-flex align-center">
                            <span class="legend-dot" :style="{ backgroundColor: $getColor('dossiers.status.Destroyed') }" /> Détruits
                        </div>
                        <div class="font-weight-bold">
                            {{ stats.records?.Destroyed || 0 }}
                        </div>
                    </div>
                </v-col>

                <v-col cols="12" md="4">
                    <div class="text-overline text-grey mb-4">
                        CHARGE DE TRAVAIL
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div>Total missions</div>
                        <div class="font-weight-bold">
                            {{ totalMissions }}
                        </div>
                    </div>
                    <div class="d-flex justify-space-between border-b pb-2 mb-2">
                        <div>Jours réalisés</div>
                        <div class="font-weight-bold">
                            {{ stats.work?.done || 0 }} j
                        </div>
                    </div>
                    <div class="d-flex justify-space-between pb-2">
                        <div>Jours estimés</div>
                        <div class="font-weight-bold">
                            {{ stats.work?.estimate || 0 }} j
                        </div>
                    </div>
                </v-col>
            </v-row>
        </v-card>

        <v-row>
            <v-col cols="12" lg="4">
                <v-card class="pa-6 fill-height d-flex flex-column" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-4">
                        Nombre de dossiers
                    </div>
                    <v-skeleton-loader v-if="loading" height="200" type="image" />
                    <template v-else>
                        <div class="chart-container flex-grow-1 my-4">
                            <Doughnut v-if="recordsChart?.labels?.length > 0" :data="recordsChart" :options="chartOptions" />
                            <div class="chart-center-text">
                                <div class="text-h4 font-weight-bold">
                                    {{ totalRecords }}
                                </div>
                                <div class="text-caption text-grey text-uppercase">
                                    TOTAL
                                </div>
                            </div>
                        </div>
                        <v-row class="mt-4" dense>
                            <v-col v-for="item in recordsLegendItems" :key="item.label" cols="6" class="d-flex align-center text-body-2">
                                <span class="legend-dot" :style="{ backgroundColor: item.color }" /> {{ item.label }}
                            </v-col>
                        </v-row>
                    </template>
                </v-card>
            </v-col>

            <v-col cols="12" lg="4">
                <v-card class="pa-6 fill-height d-flex flex-column" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-4">
                        Métrage
                    </div>
                    <v-skeleton-loader v-if="loading" height="200" type="image" />
                    <template v-else>
                        <div class="chart-container flex-grow-1 my-4">
                            <Doughnut v-if="measurementChart?.labels?.length > 0" :data="measurementChart" :options="chartOptions" />
                            <div class="chart-center-text">
                                <div class="text-h4 font-weight-bold">
                                    {{ totalMeasurement }}
                                </div>
                                <div class="text-caption text-grey text-uppercase">
                                    MÈTRES
                                </div>
                            </div>
                        </div>
                        <v-row class="mt-4" dense>
                            <v-col v-for="item in measurementLegendItems" :key="item.label" cols="6" class="d-flex align-center text-body-2">
                                <span class="legend-dot" :style="{ backgroundColor: item.color }" /> {{ item.label }}
                            </v-col>
                        </v-row>
                    </template>
                </v-card>
            </v-col>

            <v-col cols="12" lg="4">
                <v-card class="pa-6 fill-height" elevation="1" rounded="lg">
                    <div class="text-h6 font-weight-bold mb-8">
                        Statut des missions
                    </div>
                    <v-skeleton-loader v-if="loading" height="200" type="paragraph" />
                    <div v-else>
                        <div v-for="status in missionStatuses" :key="status.key" class="mb-5">
                            <div class="d-flex justify-space-between mb-2">
                                <div class="text-body-2 font-weight-medium">
                                    {{ status.label }}
                                </div>
                                <div class="text-body-2 font-weight-bold">
                                    {{ stats.missions?.[status.key] || 0 }}
                                </div>
                            </div>
                            <v-progress-linear :color="status.color" height="8" :model-value="stats.missions?.[status.key] || 0" :max="totalMissions || 1" rounded />
                        </div>
                    </div>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<style scoped>
    /* Les bordures grises pour le tableau résumé */
.border-b {
    border-bottom: 1px solid #e2e8f0 !important;
}

/* Positionnement pour mettre le texte au centre du Doughnut */
.chart-container {
    position: relative;
    height: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
}
.chart-center-text {
    position: absolute;
    text-align: center;
    pointer-events: none; /* Empêche le texte de bloquer les bulles d'info (tooltip) au survol */
}

/* Le petit carré de couleur pour les légendes */
.legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 2px;
    display: inline-block;
    margin-right: 8px;
}

.opacity-80 {
    opacity: 0.8;
}

.text-primary {
    color: #091426 !important;
}
</style>
